import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FASE_AREA, fechaHora } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Upload, FileText, ExternalLink, Paperclip } from "lucide-react";

export default function Plantillas() {
  const { user } = useAuth();
  const [pasos, setPasos] = useState(null);
  const refs = useRef({});

  const load = () => api.get("/plantillas").then((r) => setPasos(r.data));
  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const subir = async (paso_numero, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("paso_numero", paso_numero);
    try {
      await api.post("/plantillas", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Plantilla actualizada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const trigger = (n) => {
    const input = refs.current[n];
    if (input) { input.onchange = (e) => subir(n, e.target.files[0]); input.click(); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Plantillas de documentos</h1>
        <p className="text-slate-500 mt-1">
          Sube el formato en blanco de cada paso con documento obligatorio. Todos los modelos heredan estas
          plantillas: los usuarios las descargan desde el paso, las llenan y las vuelven a subir.
        </p>
      </div>

      {!pasos ? (
        <div className="space-y-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : (
        <div className="space-y-4">
          {pasos.map((p) => (
            <Card key={p.paso_numero} className="border-slate-200 shadow-sm p-5" data-testid={`plantilla-${p.paso_numero}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                    <Paperclip size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-300">{p.paso_numero}</span>
                      <h3 className="font-head font-bold tracking-tight">{p.nombre}</h3>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {FASE_AREA[p.fase]} · Formato: <span className="font-medium text-slate-500">{p.formato_label}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.plantilla ? (
                    <a href={fileUrl(p.plantilla.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 border border-slate-200 rounded-lg px-3 py-2 transition-colors" data-testid={`download-plantilla-${p.paso_numero}`}>
                      <FileText size={15} /> {p.plantilla.original_filename} <ExternalLink size={13} />
                    </a>
                  ) : (
                    <span className="text-sm text-slate-400 italic">Sin plantilla</span>
                  )}
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={() => trigger(p.paso_numero)} data-testid={`upload-plantilla-${p.paso_numero}`}>
                    <Upload size={15} className="mr-1" /> {p.plantilla ? "Reemplazar" : "Subir"}
                  </Button>
                  <input type="file" accept={p.formato_exts.map((e) => "." + e).join(",")} className="hidden" ref={(el) => (refs.current[p.paso_numero] = el)} />
                </div>
              </div>
              {p.plantilla && (
                <div className="text-[11px] text-slate-400 mt-3 pl-14">
                  v{p.plantilla.version} · Subida por {p.plantilla.uploaded_by_nombre} · {fechaHora(p.plantilla.uploaded_at)}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
